var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Button } from 'ziphy-web-shared/basic/lib/buttons';
import { useMobxForm } from 'ziphy-web-shared/basic/lib/forms';
import { Select, SignaturePad } from 'ziphy-web-shared/basic/lib/forms/fields';
import { Title, ToggleButton } from 'ziphy-web-shared/basic/lib/simple';
import { Modal, ModalToolbarBtn } from 'ziphy-web-shared/basic/lib/utilities/modals';
import { $loader } from 'ziphy-web-shared/basic/utils';
import { $settings } from 'ziphy-web-shared/basic/entities/settings';
import { $notes } from '../../Notes.store';
import styles from './Sign.module.scss';
const SignModal = ({ addendumText, onAfterSubmit, _core }) => {
    var _a;
    const { t } = useTranslation();
    const isAddendum = Boolean(addendumText);
    const form = useMobxForm(useMemo(() => ({
        initialValues: {
            signature: '',
            addendumText,
            clientNotification: true,
            reasonId: null,
        },
        fieldsConfig: {
            signature: { validate: ['required'] },
            reasonId: { validate: isAddendum ? ['required'] : [] },
        },
    }), [addendumText, isAddendum]));
    const signature = form.field('signature', '');
    const clientNotification = form.field('clientNotification', true);
    const handleSubmit = form.submit((data) => __awaiter(void 0, void 0, void 0, function* () {
        var _b, _c;
        const response = isAddendum
            ? yield $notes.addAddendum(data)
            : yield $notes.sign((() => { const { reasonId: _omit } = data, rest = __rest(data, ["reasonId"]); return rest; })());
        const error = response.error;
        if (error) {
            if (error.code === 'error.notes.state.state_updated') {
                yield $notes.refresh();
                (_c = (_b = error.data) === null || _b === void 0 ? void 0 : _b.block_refs) === null || _c === void 0 ? void 0 : _c.forEach((ref) => $notes.highlightBlock(ref, 'new'));
                _core.onHide();
            }
        }
        else {
            if (typeof onAfterSubmit === 'function') {
                yield onAfterSubmit();
            }
            _core.onHide();
        }
    }));
    const reasonId = form.field('reasonId', null);
    const reasonItems = ((_a = $settings.optionsByScope['reason_for_editing_notes']) === null || _a === void 0 ? void 0 : _a.filter(x => x.key !== 'initial').map((x) => ({
        id: x.id,
        text: x.value,
    }))) || [];
    return (_jsxs(Modal, Object.assign({ size: 'lg', centered: true, scrollable: true }, { children: [_jsx("div", Object.assign({ className: "modal-toolbar" }, { children: _jsx(ModalToolbarBtn, { type: "close", action: () => _core.onHide(false) }) })), _jsx(Modal.Header, Object.assign({ className: 'pb-0' }, { children: t(isAddendum ? 'label.sign_addendum' : 'label.sign_notes') })), _jsxs(Modal.Body, { children: [_jsxs("div", Object.assign({ className: styles.description }, { children: [isAddendum && (_jsxs(_Fragment, { children: [t('text.unlock_notes_reason'), _jsxs("form", Object.assign({ id: form.id, onSubmit: $loader.registerHandler(form.id, handleSubmit), className: "mt-20" }, { children: [_jsx(Select, { field: reasonId, mode: ['sm'], items: reasonItems, placeholder: t('label.select_unlock_reason'), search: true }), _jsx("p", { className: "mt-4" })] }))] })), _jsx("p", { children: t('text.sign_notes.attestation_statement.cosigner') })] })), _jsxs("form", Object.assign({ id: form.id, onSubmit: $loader.registerHandler(form.id, handleSubmit) }, { children: [_jsx(Title, Object.assign({ size: 'h3', className: 'mb-10 mt-20' }, { children: t('label.signature') })), _jsx(SignaturePad, { field: signature, mode: ['secondary'] })] }))] }), _jsx(Modal.Footer, { children: _jsxs("div", Object.assign({ className: styles.buttons }, { children: [_jsx(Button, Object.assign({ mode: ['primary', 'sm'], form: form.id, isLoading: $loader.isRunHandler(form.id) }, { children: t(isAddendum ? 'btn.sign_addendum' : 'btn.sign_notes') })), _jsx(Button, Object.assign({ mode: ['secondary', 'sm', 'min110'], onClick: _core.onHide }, { children: t('btn.cancel') })), _jsx(ToggleButton, { mode: ['simple', 'sm'], selected: clientNotification.value, onClick: () => clientNotification.onChange(!clientNotification.value), invalid: clientNotification.isInvalid, label: t('text.sign_notes.patient_notification'), checkbox: { mode: 'circle' }, className: styles.clientNotification })] })) })] })));
};
SignModal.displayName = 'SignModal';
export default observer(SignModal);
